import { useRefetchQuery } from "libs/reactQuery";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Modal } from "ui";

import {
  useAddDeliveryZone,
  useUpdateDeliveryFee,
  useUpdateDeliveryZone
} from "../../../apis";
import {
  getAddDeliveryZoneRequestBody,
  getUpdateDeliveryFeeRequestBody,
  getInitialDeliveryFeeFormValues
} from "../../../helpers";
import { DeliveryFeeForm, DeliveryFeeFormValues } from "../../forms";
import { DeliveryFeeModalProps } from "./DeliveryFeeModal.types";

import styles from "./DeliveryFeeModal.module.scss";

const DELIVERY_FEE_FORM_ID = "delivery-fee-form-id";

const DeliveryFeeModal = ({
  isOpen,
  onClose,
  editableFee
}: DeliveryFeeModalProps) => {
  const { t } = useTranslation();

  const refetchDeliveryFees = useRefetchQuery(["delivery-fees"]);

  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false);

  const addDeliveryZone = useAddDeliveryZone({
    onSuccess: () => {
      toast.success(t("Delivery Zone created successfully"));
      onClose();
      refetchDeliveryFees();
    }
  });

  const updateDeliveryFee = useUpdateDeliveryFee();
  const updateDeliveryZone = useUpdateDeliveryZone();

  const onFormSubmit = async (values: DeliveryFeeFormValues) => {
    if (!editableFee) {
      addDeliveryZone.mutate(getAddDeliveryZoneRequestBody(values));
      return;
    }

    const tasks: Promise<unknown>[] = [
      updateDeliveryFee.mutateAsync(getUpdateDeliveryFeeRequestBody(values))
    ];

    if (values.zoneId) {
      const zone = editableFee.deliveryZone;

      const savedAllowedIds = (zone.allowedResellers ?? []).map(
        (reseller) => reseller.id
      );
      const hasAllowlistChanged =
        savedAllowedIds.length !== values.allowedResellerIds.length ||
        savedAllowedIds.some((id) => !values.allowedResellerIds.includes(id));

      // Each key is sent only when it moved: the allowlist in particular is a
      // full replacement server-side, so sending it on every save would rewrite
      // the list even when the admin only touched a fee.
      const zonePatch = {
        ...(values.isExpedition !== zone.isExpedition
          ? { isExpedition: values.isExpedition }
          : {}),
        ...(values.isHiddenFromResellers !== zone.isHiddenFromResellers
          ? { isHiddenFromResellers: values.isHiddenFromResellers }
          : {}),
        ...(hasAllowlistChanged
          ? { allowedResellerIds: values.allowedResellerIds }
          : {})
      };

      if (Object.keys(zonePatch).length > 0) {
        tasks.push(
          updateDeliveryZone.mutateAsync({ zoneId: values.zoneId, ...zonePatch })
        );
      }
    }

    try {
      await Promise.all(tasks);
      toast.success(t("Delivery Zone updated successfully"));
      onClose();
      refetchDeliveryFees();
    } catch {
      // mutation hooks already surface server errors via toast
    }
  };

  return (
    <Modal
      formId={DELIVERY_FEE_FORM_ID}
      isOpen={isOpen}
      title={`${editableFee ? t("Edit Delivery Fee") : t("New Delivery zone")} `}
      isLoading={editableFee ? false : addDeliveryZone.isLoading}
      okText={editableFee ? t("Save") : t("Create")}
      isOkDisabled={isSubmitButtonDisabled}
      className={styles.modal}
      onClose={onClose}
    >
      <DeliveryFeeForm
        formId={DELIVERY_FEE_FORM_ID}
        defaultValues={getInitialDeliveryFeeFormValues(editableFee)}
        initialAllowedResellers={
          editableFee?.deliveryZone.allowedResellers ?? []
        }
        onSubmit={onFormSubmit}
        setIsSubmitButtonDisabled={setIsSubmitButtonDisabled}
      />
    </Modal>
  );
};

export default DeliveryFeeModal;
